import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ILandingPageProps } from '../../interfaces/IPages';

import {
    ShipContactForm,
    IContactFormValues,
} from '../components/sections/ship-contact-form/ship-contact-form';

import { LocaleConfigContext } from '../contexts/locale-config/locale-config';

import { submitShipContactForm, ISubmitContactFormProps } from '../api/resources';
import { DE_COUNTRY_CODE } from '../contexts/booking/constants';
import { getApiValidationErrors } from '../contexts/booking/helpers';

export const createUniqueId = () => Math.random().toString(36).substr(2, 16);

const errorMessageMap: Record<string, string> = {
    firstName: 'error.firstName',
    lastName: 'error.lastName',
    emailAddress: 'error.emailError',
    phoneNumber: 'error.phoneNumber',
};

const FormSchema = Yup.object().shape({
    firstName: Yup.string().required('error.firstName').default(''),
    lastName: Yup.string().required('error.lastName').default(''),
    emailAddress: Yup.string().required('error.emailError').email('error.emailError').default(''),
    phoneCode: Yup.string().required().default(''),
    phoneNumber: Yup.string()
        .required('error.phoneNumber')
        .min(5, 'error.phoneNumber')
        .max(15, 'error.phoneNumber')
        .default(''),
    message: Yup.string().default(''),
});

export const ShipContactMod = ({ getLandingPage }: ILandingPageProps['pageProps']) => {
    const { code, url } = React.useContext(LocaleConfigContext);

    const [formSubmitState, setFormSubmitState] = React.useState<
        'error' | 'success' | 'indefinite'
    >('indefinite');
    const [isLoading, setIsLoading] = React.useState(false);

    const getRequestData = (userData: IContactFormValues): ISubmitContactFormProps => {
        return {
            channel: 'NoOnlineRuleApplies',
            firstName: userData.firstName,
            lastName: userData.lastName,
            phoneNumber: `${userData['phoneCode']}${userData['phoneNumber']}`,
            emailAddress: userData.emailAddress,
            domain: url,
            userId: null,
            sessionId: null,
            message: userData.message,
            bookingCode: createUniqueId(),
            contactMethod: 'ContactForm',
            bookingUrl: window.location.href,
        };
    };

    const formik = useFormik({
        initialValues: FormSchema.getDefault(),
        validationSchema: FormSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async (values, { setErrors }) => {
            setFormSubmitState('indefinite');
            setIsLoading(true);
            const requestData = getRequestData(values);

            try {
                const response = await submitShipContactForm(requestData);

                if (response?.isSuccessful) {
                    setFormSubmitState('success');
                }
            } catch (error) {
                const errors = getApiValidationErrors(error);

                if (!errors.length) {
                    setFormSubmitState('error');
                    return;
                }

                const formikErrors = errors.reduce((acc, currentValue) => {
                    let key = currentValue.field;

                    if (!values[key] || !(key in errorMessageMap)) {
                        return acc;
                    }

                    return {
                        ...acc,
                        // here we are using error message map because BE not returning
                        // translationKeyV2 on this endpoint for now
                        [key]: errorMessageMap[key],
                    };
                }, {});

                setErrors(formikErrors);
                setFormSubmitState('error');
            } finally {
                setIsLoading(false);
            }
        },
    });

    const handleSubmit = () => {
        formik.submitForm();
    };

    const handleFieldChange = (name: string, val: string) => {
        const validate = Boolean(formik.errors[name]);
        formik.setFieldValue(name, val, validate);
    };

    return (
        <ShipContactForm
            isLoading={isLoading}
            formSubmitState={formSubmitState}
            cruiseTitle={getLandingPage.title}
            countryCode={code ? code.toUpperCase() : DE_COUNTRY_CODE}
            errors={formik.errors}
            onFieldChange={handleFieldChange}
            onSubmit={handleSubmit}
        />
    );
};
